/* eslint-disable @typescript-eslint/prefer-for-of */
/* eslint-disable prefer-arrow/prefer-arrow-functions */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import urlJoin from 'proper-url-join';
import { LibFormManagerService } from 'tuain-ng-forms-lib';
import { StorageService } from './storage.service';
import { APIGatewayService } from './api-gateway.service';
import { EventManagerService } from './event-manager.service';
import { defaultForms } from './form-manager.service.config';
import { ComponentPageService } from 'src/app/services/component-pages.service';
import { defaultConfig } from 'src/app/app.general';

const SYNC = 0;
const UPDATE_ALL = 1;
const SIGNATURE = 'sign';
const FORM_DEF = 'def';
const COMPARISON_FUNCTIONS_FIELD = 'functionCode';
const CUSTOMIZE = false;
const EXEC_START = false;
const VERSION = 'TUAIN-FRM-2021A';

const DEFAULT = 'DEFAULT';
const SESSIONLESS_HOME = 'SESSIONLESS_HOME';
const LOGIN = 'LOGIN';
const UNAUTHORIZED = 'UNAUTHORIZED';

function userAllowed(requiredFunctions, userFunctions, matchFld = null) {
  for (let index = 0; index < requiredFunctions?.length; index++) {
    const requiredFunction = requiredFunctions[index];
    const hasFunction = userFunctions?.find(
      usrFnc => (matchFld ? usrFnc[matchFld] : usrFnc) === requiredFunction);
    if (!hasFunction) {
      return false;
    }
  }
  return true;
}

function supportedStates(formDefinition, states, userFunctions) {
  const formFunctions = formDefinition?.form?.functions;
  if (!formFunctions || formFunctions?.length === 0) {
    return states;
  }
  const functionsForStates = {};
  for (let index = 0; index < formFunctions.length; index++) {
    const functionDef = formFunctions[index];
    const { functionCode, modes } = functionDef;
    const statesFunction = modes?.split(',')?.map(state => state.trim()) ?? [];
    statesFunction.forEach((state) => {
      if (!functionsForStates[state]) {
        functionsForStates[state] = [];
      }
      functionsForStates[state].push(functionCode);
    });
  }
  const userStates = [];
  const allStates = Object.keys(functionsForStates);
  for (let index = 0; index < allStates.length; index++) {
    const state = allStates[index];
    if (userAllowed(functionsForStates[state], userFunctions)) {
      userStates.push(state);
    }
  }
  if (userStates.length === 0) {
    return null;
  }
  formDefinition.form.formModes = userStates;
  delete formDefinition.form.functions;
  return userStates;
}

function removeUnsupportedActions(actions, userFunctions) {
  const actIndexesToRemove = [];
  for (let index = 0; index < actions?.length; index++) {
    const actionFunctions = actions[index]?.functions ?? [];
    if (!actionFunctions.every(fnc => userFunctions?.includes(fnc))) {
      actIndexesToRemove.unshift(index);
    }
    delete actions[index].functions;
  }
  for (let index = 0; index < actIndexesToRemove.length; index++) {
    actions.splice(actIndexesToRemove[index], 1);
  }
  return actions;
}

function removeUnsupportedTableActions(tables, userFunctions) {
  for (let index = 0; index < tables?.length; index++) {
    const table = tables[index];
    const actIndexesToRemove = [];
    for (let actIndex = 0; actIndex < table?.actions?.length; actIndex++) {
      const actionFunctions = table.actions[actIndex]?.functions ?? [];
      // COMPARISON_FUNCTIONS_FIELD
      if (!userAllowed(actionFunctions, userFunctions)) {
        actIndexesToRemove.unshift(actIndex);
      }
      delete table.actions[actIndex].functions;
    }
    for (let actIndex = 0; actIndex < actIndexesToRemove.length; actIndex++) {
      table.actions.splice(actIndexesToRemove[actIndex], 1);
    }
  }
  return tables;
}

function customizeFormDefinition(formDefinition, userFunctions) {
  const { transitions, actions, tables } = formDefinition;
  const states = formDefinition?.states?.map(state => state.name);
  const userStates = supportedStates(formDefinition, states, userFunctions);
  formDefinition.states = states?.filter(state => userStates?.includes(state)) ?? [];
  formDefinition.transitions =
    transitions
      ?.map((transition) => {
        const { name, source, destination } = transition;
        return { name, source, destination };
      })
      .filter(
        trns => userStates?.includes(trns.source) && userStates?.includes(trns.destination),
      ) ?? [];
  formDefinition.actions = removeUnsupportedActions(actions, userFunctions) ?? [];
  formDefinition.tables = removeUnsupportedTableActions(tables, userFunctions);
  return formDefinition;
}
@Injectable({ providedIn: 'root' })
export class FormOperationService extends LibFormManagerService {
  encryptKey: string;
  signatures: any;
  state: number;
  userFunctions: string[];
  constructor(
    private _apiGateway: APIGatewayService,
    private _router: Router,
    private _eventManager: EventManagerService,
    private _storageService: StorageService,
    private _componentPageService: ComponentPageService,
  ) {
    super();
    this.state = SYNC;
    this.subscribeEvents();
    this.getFormSignatures();
  }

  override async loadStack() {
    this.pageStack = this._storageService?.getItem('pageStack') ?? [];
  }

  override saveStack() {
    this._storageService.setItem('pageStack', this.pageStack);
  }

  subscribeEvents() {
    this._eventManager.subscribe('sessionEncryptKeyReceived', data => this.updateEncryptKey(data));
    this._eventManager.subscribe('notAuthorized', () => this.goToForm(UNAUTHORIZED));
    this._eventManager.subscribe('startApp', async (obj) => { this.goToForm(DEFAULT);
    });
    this._eventManager.subscribe('sessionEstablished', (data) => {
      this.getFormSignatures();
      this.userFunctions = data?.privileges ?? this.userFunctions;
      return data?.changePage && this.goToForm(data?.nextForm?.[0] || DEFAULT, null, data?.nextForm?.[1]);
    });
    this._eventManager.subscribe('sessionEnded', () => {
      this._apiGateway.closeSession();
      this.goToForm(SESSIONLESS_HOME);
    });
  }

  updateEncryptKey(encryptKey) {
    this.encryptKey = encryptKey;
  }

  async getFormSignatures() {
    const formSignaturesRes = await this._apiGateway.getSignatures();
    this.signatures = formSignaturesRes.getData();
    const codes = Object.keys(this.signatures ?? {});
    if (codes.length === 0) {
      this.state = UPDATE_ALL;
      return;
    }
    codes.forEach((code) => {
      const remoteSignature = this.signatures[code];
      const storedSignature = this.getFormSignature(code);
      if (storedSignature !== remoteSignature) {
        this.removeForm(code);
        this.updateSignature(code, remoteSignature);
      }
    });
  }

  override async getFormDefinition(code) {
    try {
      const storedDefinition = this.getForm(code);
      if (storedDefinition && this.state !== UPDATE_ALL) {
        return customizeFormDefinition(storedDefinition, this.userFunctions);
      }
      this._eventManager.next('serverProcessStarted', null);
      const defRes = await this._apiGateway.getFormDefinition({
        formCode: code,
        customize: CUSTOMIZE,
        executeStart: EXEC_START,
        version: VERSION,
      });
      this._eventManager.next('serverProcessEnded', null);
      if (defRes && !defRes.hasError()) {
        const definition = defRes.getData();
        this.updateForm(code, definition);
        return customizeFormDefinition(definition, this.userFunctions);
      }
      // No se tiene definición local de formulario ni se obtuvo del server
      return defaultForms[code]
        ? customizeFormDefinition(defaultForms[code], this.userFunctions)
        : null;
    } catch (e) {
      console.log(`Se produjo una excepción obteniendo Formulario ${code}: ${e}`);
    }
    return null;
  }

  override async execServerAction(actionRequest) {
    try {
      this._eventManager.next('serverProcessStarted', null);
      const formActionResponse = await this._apiGateway.executeFormAction(actionRequest);
      this._eventManager.next('serverProcessEnded', null);
      if (formActionResponse && formActionResponse.data) {
        return formActionResponse;
      }
    } catch (e) {
      console.log(`Excepción solicitando acción ${actionRequest}: ${e}`);
    }
    return null;
  }

  override goToForm(formName, token = null, subject = null) {
    const { logoutPath, serverEntryPoint } = defaultConfig;
    if (logoutPath && (formName === SESSIONLESS_HOME || formName === LOGIN)) {
      let protocol = serverEntryPoint.protocol ?? window.location.protocol;
      protocol = protocol.endsWith(':') ? protocol.slice(0, -1) : protocol;
      let server = serverEntryPoint.server ?? window.location.hostname;
      server = server.endsWith('/') ? server.slice(0, -1) : server;
      let baseUrl = `${protocol}://${server}`;
      const port = serverEntryPoint.port ?? window.location.port;
      baseUrl += port ? `:${port}` : '';
      const logoutUrl = urlJoin(baseUrl, defaultConfig?.logoutPath);
      window.open(logoutUrl, '_self');
      return;
    }
    const formRoute = this._componentPageService.formRoute(formName);
    const navigationArray = [formRoute];
    (token || subject) && navigationArray.push(token ?? 0);
    subject && navigationArray.push(subject);
    this._router.navigate(navigationArray);
  }

  getForm(code: string) {
    return this._storageService.getItem(`${FORM_DEF}-${code}`);
  }

  getFormSignature(code: string) {
    return this._storageService.getItem(`${SIGNATURE}-${code}`);
  }

  updateSignature(code: string, value) {
    this._storageService.setItem(`${SIGNATURE}-${code}`, value);
  }

  removeForm(code: string) {
    return this._storageService.removeItem(`${FORM_DEF}-${code}`);
  }

  updateForm(code: string, def) {
    this._storageService.setItem(`${FORM_DEF}-${code}`, def);
  }
}
